import { autoinject, bindable, computedFrom, observable } from 'aurelia-framework';
import { values } from 'lodash';
import { IDroppableHandler } from '../../components/drag-and-drop/models';
import { ResourceService } from '../../utils/resourceService';
import { IImportPreviewRowAM, ImportMode, ImportService } from './import-api';
import { ImportType } from './import-view';
import { NotificationType, ResourcedNotificationService } from '../../components/notifications/notification-service';

@autoinject
export class ImportFileDrop {
	@bindable scopeId: string;
	@bindable importType: string;
	@bindable fileDrop: (params: any) => void;
	@bindable cancel: () => void;

	@observable files: FileList;

	dragging = false;
	hasFile = false;
	file: File[];
	fileUpload: HTMLElement;
	fileContent: any;
	rows: IImportPreviewRowAM[];
	error: IImportFileErrorAM;
	loading = false;
	fileInputValue: any;
	@bindable invertSignsForFinancialEntries: boolean;
	importMode: ImportMode = 'append';
	importModes: ImportMode[] = ['append', 'replace', 'overwrite'];

	droppableHandler: IDroppableHandler = new ImportFileDroppableHandler(this);

	constructor(private importService: ImportService, private resourceService: ResourceService, private notificationService: ResourcedNotificationService) {
	}

	attached() {
		window.addEventListener('dragover', this.preventWindowDndEvents, false);
		window.addEventListener('drop', this.preventWindowDndEvents, false);
	}

	detached() {
		window.removeEventListener('dragover', this.preventWindowDndEvents);
		window.removeEventListener('drop', this.preventWindowDndEvents);
	}

	@computedFrom('file')
	get fileName(): string {
		if (this.file == null || this.file.length === 0) {
			return '';
		} else if (this.file.length === 1) {
			return this.file[0].name;
		} else {
			return this.resourceService.getResource('ImportFileDrop_Text_MultipleFiles');
		}
	}

	filesChanged(newValue: FileList) {
		this.stopDragging();
		this.uploadFile(values<File>(newValue));
	}

	openFileDialog() {
		this.fileUpload.click();
	}

	async uploadFile(files: File[]) {
		const handleResponse = (response: Response) => {
			this.loading = false;
			if (response.status === 400) {
				response.json().then(error => { this.error = error; });
			} else {
				response.json().then(rows => {
					this.rows = rows;
					if (this.fileDrop) { this.fileDrop({ rows }); }
				});
			}
		};

		this.file = files;
		this.hasFile = true;
		this.loading = true;
		this.error = null;
		this.fileInputValue = null; // bound to the input html element, make null to enable uploading the same file path again

		let response: Response;

		switch (this.importType) {
			case ImportType.FinancialEntries:
				response = await this.importService.previewFinancialEntryFile(this.file);
				break;
			case ImportType.MeasureEntries:
				response = await this.importService.previewMeasureEntryFile(this.file);
				break;
			default:
				throw new Error(`Import type ${this.importType} is not supported.`);
		}

		handleResponse(response);
	}

	startDragging() {
		this.dragging = true;
	}

	stopDragging() {
		this.dragging = false;
	}

	cancelFile() {
		this.file = null;
		this.hasFile = false;
		this.importMode = 'append';
		if (this.cancel) {
			this.cancel();
		}
	}

	fileSelected(event: Event) {
		const reader = new FileReader();
		const file = (event.target as HTMLInputElement).files[0];
		reader.readAsDataURL(file);
		reader.onload = () => {
			this.fileContent = reader.result;
		};
		this.hasFile = true;
	}

	async sendFile() {
		const handleResponse = async (response: Response) => {
			// A duplicate file is a bare 400, which reaches the client as { error: { message: "Bad request." } }
			// (status code re-execution in Startup); an invalid mode is a 400 with a top-level message
			const body = await response.json().catch(() => null);
			if (response.ok && body && body.id) {
				location.href = `/imports/entry/${body.id}`;
				return;
			}

			this.loading = false;
			if (body && body.message) {
				this.notificationService.pushWithLiteralContent("ImportFileDrop_Error_SendFailed", NotificationType.Error, body.message);
			} else if (response.status === 400) {
				this.notificationService.push("ImportFileError_DuplicateFileName", NotificationType.Error, null, [this.file[0].name], null);
			} else {
				this.notificationService.push("ImportFileDrop_Error_SendFailed", NotificationType.Error);
			}
		};

		this.loading = true;

		let response: Response;

		switch (this.importType) {
			case ImportType.FinancialEntries:
				response = await this.importService.sendFinancialEntryFile(this.scopeId, this.file, this.invertSignsForFinancialEntries, this.importMode);
				break;
			case ImportType.MeasureEntries:
				response = await this.importService.sendMeasureEntryFile(this.scopeId, this.file);
				break;
			default:
				throw new Error(`Value ${this.importType} is not a valid import type.`);
		}

		handleResponse(response);
	}

	private preventWindowDndEvents(event: DragEvent) {
		event.preventDefault();
	}

	importTypeChanged() {
		this.file = null;
		this.hasFile = false;
		this.fileContent = null;
		this.rows = null;
		this.error = null;
		this.loading = false;
		this.fileInputValue = null;
		this.importMode = 'append';
	}

	handleKeydown(event: KeyboardEvent) {
		// Check if the pressed key is Enter or Space
		if (event.key === 'Enter' || event.key === ' ') {
			// Prevent the default action to stop scrolling when space is pressed
			event.preventDefault();
			this.openFileDialog();
		} else {
			return true;
		}
	}
}

class ImportFileDroppableHandler implements IDroppableHandler {
	constructor(private importFileDrop: ImportFileDrop) {
	}

	onDragEnter(event: DragEvent, dragTarget: any): void {
		this.importFileDrop.startDragging();
	}
	onDragLeave(event: DragEvent, dragTarget: any): void {
		this.importFileDrop.stopDragging();
	}
	onDrop(event: DragEvent, dropTarget: any): boolean {
		this.importFileDrop.stopDragging();
		this.importFileDrop.uploadFile(values<File>(event.dataTransfer.files));
		return false;
	}
}

interface IImportFileErrorAM {
	message: string;
}
